import { useState } from 'react';
import type { CreateBankDepositRequest } from '@six-pockets/shared';
import { MoneyInput } from '@/components/ui';
import { ManageFormModal } from '@/features/manage/components/ManageFormModal';
import { parseWhole } from '@/features/manage/draft';
import { formatINR } from '@/utils/format';

interface BankDepositModalProps {
  /** Money at the cash counter now; a deposit can't be more. Null before any cash count. */
  atCounter: number | null;
  busy: boolean;
  error: string | null;
  onSave: (body: CreateBankDepositRequest) => void;
  onClose: () => void;
}

/** Deposit to bank: how much cash is taken from the counter. Mount it only while open. */
export function BankDepositModal({ atCounter, busy, error, onSave, onClose }: BankDepositModalProps) {
  const [amount, setAmount] = useState('');
  const value = parseWhole(amount);
  const available = Math.max(0, atCounter ?? 0);

  const blocked =
    atCounter === null ? "Log today's cash count first"
    : !value ? 'Enter the amount in whole rupees'
    : value > available ? `Only ${formatINR(available)} is at the cash counter`
    : undefined;

  return (
    <ManageFormModal
      title="Deposit to bank"
      submitLabel={value && !blocked ? `Deposit ${formatINR(value)}` : 'Deposit'}
      busy={busy}
      error={error}
      blockedReason={blocked}
      onSubmit={() => onSave({ amount: value! })}
      onClose={onClose}
    >
      <MoneyInput
        label="Cash taken to the bank"
        hint={atCounter === null ? "Log today's cash count first" : `${formatINR(available)} at the cash counter`}
        tone={value !== null && value > available ? 'warning' : 'default'}
        value={amount}
        onChange={setAmount}
        data-autofocus
      />
    </ManageFormModal>
  );
}
